/**
 * Typed client for the control plane's HTTP routes (apps/control/src/app.ts).
 *
 * Every call is same-origin with the `__Host-` session cookie; mutating calls send JSON (the
 * Worker checks Origin). Routes that apps/control does not serve yet are grouped under
 * "hub-ext" and listed in apps/web/README.md › API assumptions.
 */
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import type {
  DeviceEndorsement,
  DispatchGrant,
  EcPublicJwk,
  EnrollmentApproval,
  GrantRevocation,
  SignedCommand,
  SignedRevocationList,
} from '@viko/crypto'
import { canonicalize } from '@viko/crypto'
import {
  AnswerClaimResult,
  ContentDirectoryPage,
  ContentDirectoryPublish,
  ContentDirectoryRead,
  ContentIdentityRead,
  ContentPutResult,
  DaemonContentIdentityProof,
  EncryptedContent,
  EncryptionContext,
  type LoginJoin,
  type MachineLoadFrame,
  type MachinePresenceFrame,
  type MachineProjectsFrame,
  PrivateNotification,
  ProviderAccountsMetadata,
  type RunUsageSummary,
  type TranscriptEvent,
} from '@viko/protocol'
import { clock } from './clock.js'
import type { AuthenticationResponseJSON, RegistrationResponseJSON } from './passkeys.js'
import { retryAfterHeader } from './request-meter.js'
import type {
  Artifact,
  BrainCommand,
  BuildCard,
  CaptureResult,
  Card,
  CardAction,
  CardOp,
  CommandOutcome,
  CompareItem,
  CompareRequest,
  Credential,
  Device,
  DoctorCheck,
  GrantRow,
  HistoryRun,
  HygieneReport,
  Idea,
  InboxItem,
  Landing,
  Machine,
  Metrics,
  PreparedCommand,
  Project,
  ProjectConfigLite,
  RankedInbox,
  Rule,
  RunSummary,
  SchedMachine,
  SearchHit,
  SessionInfo,
  Settings,
  StatusAnswer,
  TaskDetail,
  UsageWindow,
  WorkstreamDetail,
} from './types.js'

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>

export class ApiError extends Error {
  /** burst-guard: the hub's `retryAfterMs` (429 body or Retry-After header). */
  readonly retryAfterMs?: number
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
    retryAfterMs?: number,
  ) {
    super(message || code)
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs
  }
}

/** GET /api/budget: the account's daily hub request budget (UTC day). */
export interface HubBudget {
  day: string
  used: number
  budget: number
  degraded: boolean
  /** Alert thresholds (percent of budget) already crossed today. */
  alerts: number[]
}

export interface SetupResult {
  credentialId: string
  credentials: number
  setupState: string
  remaining: number
  recoveryCodes: string[] | null
}

export interface Invite {
  recipientEmail: string | null
  id: string
  note: string | null
  createdAt: number
  expiresAt: number
  status: 'active' | 'used' | 'expired' | 'revoked' | 'unknown'
}

export interface CreatedInvite {
  recipientEmail: string
  id: string
  code: string
  url: string
  setupUrl: string
  note: string | null
  expiresAt: number
}

/** The hub's answer to attaching a daemon-initiated claim. */
export interface EnrollAttach {
  code: string
  purpose: 'setup' | 'enroll'
  /** `local`: vikod opened this page on its own machine; `remote`: another device (QR). */
  mode: 'local' | 'remote'
  /** This browser and the machine share a public IP. */
  sameNetwork: boolean
  country: string | null
  platform: string | null
  machineName: string
  machineId: string
  status: string
}

export interface EnrollStart {
  code: string
  token: string
  expiresAt: number
}

export interface EnrollPeek {
  status: 'pending' | 'claimed' | 'approved' | 'expired' | string
  issuedAt: number
  expiresAt: number
  machineId: string | null
  machineName: string | null
  platform: string | null
  fingerprint: string | null
  daemonKeyId: string | null
  daemonPublicKey: EcPublicJwk | null
  credentials: { credentialId: string; cose: string; label?: string; addedAt: number }[]
  credentialSetHash: string
  rpId: string
}

export interface RunEventsPage {
  events: TranscriptEvent[]
  lastSeq: number
  hasMore: boolean
}

export interface UploadTicket {
  artifactId: string
  uploadUrl: string
  url?: string
}

export class Api {
  constructor(
    private readonly fetcher: Fetcher,
    private readonly origin = '',
  ) {}

  private async req<T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders: Record<string, string> = {},
    rawBody?: BodyInit,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    const init: RequestInit = {
      method,
      credentials: 'same-origin',
      headers,
      ...(rawBody !== undefined
        ? { body: rawBody }
        : body === undefined
          ? {}
          : { body: JSON.stringify(body) }),
    }
    let res: Response
    try {
      res = await this.fetcher(this.origin + path, init)
    } catch (e) {
      throw new ApiError(0, 'network', (e as Error)?.message || 'Network unavailable')
    }
    const date = res.headers.get('Date')
    if (date) {
      const t = Date.parse(date)
      if (Number.isFinite(t)) clock.setServerTime(t)
    }
    const text = await res.text()
    const data = text ? safeJson(text) : undefined
    if (!res.ok) {
      const d = (data ?? {}) as {
        error?: string
        code?: string
        message?: string
        retryAfterMs?: unknown
      }
      const retryAfterMs =
        typeof d.retryAfterMs === 'number' && d.retryAfterMs >= 0
          ? d.retryAfterMs
          : res.status === 429
            ? (retryAfterHeader(res) ?? 1_000)
            : undefined
      throw new ApiError(
        res.status,
        d.error ?? d.code ?? `http_${res.status}`,
        d.message,
        retryAfterMs,
      )
    }
    return data as T
  }

  // ---- auth -----------------------------------------------------------------------------------
  completeEmail = (email: string) =>
    this.req<{ email: string; emailVerified: false }>('POST', '/api/auth/profile', { email })

  session = () => this.req<SessionInfo>('GET', '/api/auth/session')
  loginOptions = () =>
    this.req<PublicKeyCredentialRequestOptionsJSON>('POST', '/api/auth/login/options', {})
  /** `join`: single-ceremony sign-in (protocol LoginJoin) — the same tap endorses this browser. */
  loginVerify = (response: AuthenticationResponseJSON, join?: LoginJoin) =>
    this.req<{ authenticated: boolean }>('POST', '/api/auth/login/verify', {
      response,
      ...(join ? { join } : {}),
    })
  logout = () => this.req<{ loggedOut: boolean }>('POST', '/api/auth/logout', {})
  /** A recovery kit sends all its sign-in codes; the hub consumes the first unused one. */
  recover = (userId: string, code: string | string[]) =>
    this.req<{ scope: 'recovery' }>(
      'POST',
      '/api/auth/recover',
      Array.isArray(code) ? { userId, codes: code } : { userId, code },
    )

  /** Redeem an invite: a new account and its short setup session (cookie). */
  signup = (invite: string) =>
    this.req<{ accountId: string; setupState: string }>('POST', '/api/auth/signup', { invite })
  /** Passkey registration during sign-up (setup session). */
  setupOptions = (userName: string) =>
    this.req<PublicKeyCredentialCreationOptionsJSON>('POST', '/api/auth/setup/options', {
      userName,
    })
  setupVerify = (response: RegistrationResponseJSON, name: string) =>
    this.req<SetupResult>('POST', '/api/auth/setup/verify', { response, name })

  // ---- invites (Settings → Invites) ----------------------------------------------------------
  invites = () => this.req<Invite[]>('GET', '/api/invites')
  createInvite = (recipientEmail: string, note?: string) =>
    this.req<CreatedInvite>('POST', '/api/invites', { recipientEmail, note })
  revokeInvite = (id: string) =>
    this.req<{ revoked: string }>('DELETE', `/api/invites/${encodeURIComponent(id)}`)

  stepUpOptions = () =>
    this.req<PublicKeyCredentialRequestOptionsJSON>('POST', '/api/auth/step-up/options', {})
  stepUpVerify = (response: AuthenticationResponseJSON) =>
    this.req<{ steppedUp: boolean }>('POST', '/api/auth/step-up/verify', { response })

  credentials = () => this.req<Credential[]>('GET', '/api/auth/credentials')
  credentialOptions = (userName: string) =>
    this.req<PublicKeyCredentialCreationOptionsJSON>('POST', '/api/auth/credentials/options', {
      userName,
    })
  credentialVerify = (response: RegistrationResponseJSON, name: string) =>
    this.req<{ credentialId: string }>('POST', '/api/auth/credentials/verify', { response, name })
  deleteCredential = (id: string) =>
    this.req<{ deleted: string }>('DELETE', `/api/auth/credentials/${encodeURIComponent(id)}`)

  // ---- devices ----------------------------------------------------------------------------------
  devices = () => this.req<Device[]>('GET', '/api/devices')
  registerDevice = (endorsement: DeviceEndorsement) =>
    this.req<unknown>('POST', '/api/devices', endorsement)
  revokeDevice = (id: string) =>
    this.req<{ revoked: string }>('DELETE', `/api/devices/${encodeURIComponent(id)}`)
  applyRevocations = (signed: SignedRevocationList) =>
    this.req<unknown>('POST', '/api/revocations', signed)

  // ---- daemons / machines -----------------------------------------------------------------------
  enrollStart = () => this.req<EnrollStart>('POST', '/api/daemons/enroll', {})
  /** Attach a `viko setup` / `viko enroll` link's pending claim to this account. */
  enrollAttach = (link: string) =>
    this.req<EnrollAttach>('POST', '/api/daemons/enroll/attach', { link })
  enrollPeek = (code: string) =>
    this.req<EnrollPeek>('GET', `/api/daemons/enroll/${encodeURIComponent(code)}`)
  enrollConfirm = (code: string, fingerprint: string, approval: EnrollmentApproval) =>
    this.req<{ machineId: string }>('POST', '/api/daemons/enroll/confirm', {
      code,
      fingerprint,
      approval,
    })
  machines = async () =>
    (await this.req<Record<string, unknown>[]>('GET', '/api/machines')).map(normalizeMachine)
  /**
   * Projects each machine registered with `viko project add`. Encrypted accounts only: the
   * list travels in the machine's end-to-end encrypted report (installPrivateReports).
   */
  machineProjects = async (): Promise<{ machineId: string; report: MachineProjectsFrame }[]> => []
  /** g4-cost: per-run usage summaries from each machine's encrypted `run.usage` report. */
  runUsages = async (
    _machineIds?: readonly string[],
  ): Promise<{ machineId: string; runs: RunUsageSummary[]; ts: number }[]> => []
  // ---- lock-follows-os: the machine's encrypted screen-lock report (installPrivateReports) ----
  machinePresence = async (_machineId: string): Promise<MachinePresenceFrame | undefined> =>
    undefined
  // ---- end lock-follows-os ----
  /** Workstation load (G1): encrypted accounts only, from each machine's `machine.load` report. */
  machineLoad = async (): Promise<{ machineId: string; report: MachineLoadFrame }[]> => []
  revokeMachine = (id: string) =>
    this.req<{ revoked: string }>('DELETE', `/api/machines/${encodeURIComponent(id)}`)

  readContentIdentity = async (machineId: string) =>
    DaemonContentIdentityProof.parse(
      await this.req(
        'POST',
        '/api/content/identity/read',
        ContentIdentityRead.parse({ machineId }),
      ),
    )
  publishContentDirectory = (raw: unknown) =>
    this.req<{ revision: number; digest: string }>(
      'POST',
      '/api/content/directory/publish',
      ContentDirectoryPublish.parse(raw),
    )
  readContentDirectory = async (raw: unknown) =>
    ContentDirectoryPage.parse(
      await this.req('POST', '/api/content/directory/read', ContentDirectoryRead.parse(raw)),
    )
  // ---- f-keys: recovery, trust and key rotation (validated in content-keys-api.ts) ----
  contentKeys = (operation: string, body: unknown = {}) =>
    this.req<unknown>('POST', `/api/content/keys/${operation}`, body)
  // ---- end f-keys ----

  /** Explicit ciphertext lane. Strict validation prevents accidental plaintext fields. */
  putContent = async (raw: EncryptedContent) => {
    const content = EncryptedContent.parse(raw)
    return ContentPutResult.parse(await this.req('POST', '/api/content/put', content))
  }
  getContent = async (raw: EncryptionContext) => {
    const context = EncryptionContext.parse(raw)
    const content = EncryptedContent.parse(await this.req('POST', '/api/content/get', context))
    if (canonicalize(content.context) !== canonicalize(context))
      throw new Error('wrong content context')
    return content
  }

  // ---- runs, inbox, search, commands --------------------------------------------------------------
  runs = async () =>
    (await this.req<Record<string, unknown>[]>('GET', '/api/runs')).map(normalizeRun)
  runEvents = (runId: string, q: { after?: number; before?: number; limit?: number } = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v))
    const qs = p.toString()
    return this.req<RunEventsPage>(
      'GET',
      `/api/runs/${encodeURIComponent(runId)}/events${qs ? `?${qs}` : ''}`,
    )
  }
  inbox = () => this.req<InboxItem[]>('GET', '/api/inbox')
  /** Cross-transcript FTS (live, imported and adopted runs), optionally scoped. */
  search = (q: string, scope: { projectId?: string; source?: string; limit?: number } = {}) =>
    this.req<SearchHit[]>('GET', `/api/search${qs({ q, ...scope })}`)
  /** Read-only imported/adopted sessions of a project, newest first. */
  projectHistory = (projectId: string, q: { limit?: number; before?: number } = {}) =>
    this.req<{ runs: HistoryRun[] }>('GET', `/api/projects/${enc(projectId)}/history${qs(q)}`)
  /** The `run.start{resumeNativeSessionId}` this device signs to continue a session in Viko. */
  resumeRun = (runId: string, prompt?: string) =>
    this.req<{ command: PreparedCommand }>(
      'POST',
      `/api/runs/${enc(runId)}/resume`,
      prompt ? { prompt } : {},
    )
  /** HTTP twin of the WS `command` frame (used when the socket is down). */
  submitCommand = (command: SignedCommand) =>
    this.req<CommandOutcome>('POST', '/api/commands', command)
  commandStatus = async (id: string) => {
    const row = await this.req<CommandOutcome & { id?: string }>(
      'GET',
      `/api/commands/${encodeURIComponent(id)}`,
    )
    return { ...row, commandId: row.commandId ?? row.id ?? '' }
  }

  // ---- push & artifacts ---------------------------------------------------------------------------
  vapidKey = () => this.req<{ publicKey: string }>('GET', '/api/push/vapid-public-key')
  pushSubscribe = (subscription: PushSubscriptionJSON, deviceId?: string) =>
    this.req<unknown>('POST', '/api/push/subscriptions', { subscription, deviceId })
  pushUnsubscribe = (endpoint: string) =>
    this.req<unknown>('DELETE', '/api/push/subscriptions', { endpoint })
  artifactUrl = (id: string) =>
    this.req<{ url: string; expiresAt: number }>(
      'GET',
      `/api/artifacts/${encodeURIComponent(id)}/url`,
    )

  // ---- work model, inbox, capture, status (apps/control hub/ops.ts) -----------------------------
  /**
   * Presigned PUT on the artifact origin for a composer/capture attachment. Without R2 the hub
   * answers 503 `storage_not_configured` (→ `StorageNotConfigured`).
   */
  artifactUpload = (meta: { name: string; mime: string; bytes: number; runId?: string }) =>
    this.req<UploadTicket>('POST', '/api/artifacts', meta).catch(storageGuard)
  artifacts = (q: { projectId?: string; taskId?: string; runId?: string; group?: string } = {}) =>
    this.req<Artifact[]>('GET', `/api/artifacts${qs(q)}`)
  /** A Compare card over 2–26 artifacts, labelled A / B1, B2 … (Inbox + gallery). */
  compareArtifacts = (body: CompareRequest) =>
    this.req<{ cardId: string; title: string; items: CompareItem[] }>(
      'POST',
      '/api/artifacts/compare',
      body,
    )
  /** "A, A, C1" → the `run.message` commands (one per owning run) to sign; resolves the card. */
  pick = (cardId: string, reply: string) =>
    this.req<{
      picks: { token: string; artifacts: { artifactId: string; label: string; name: string }[] }[]
      text: string
      commands: PreparedCommand[]
    }>('POST', `/api/cards/${enc(cardId)}/pick`, { reply })

  projects = () => this.req<Project[]>('GET', '/api/projects')
  project = (id: string) => this.req<Project>('GET', `/api/projects/${enc(id)}`)
  createProject = (body: { yaml?: string; config?: ProjectConfigLite }) =>
    this.req<Project>('POST', '/api/projects', body)
  updateProject = (
    id: string,
    body: {
      yaml?: string
      config?: ProjectConfigLite
      settings?: Project['settings']
      status?: 'active' | 'archived'
      tab?: Project['tab']
      setup?: Project['setup'] // g5-setup (encrypted accounts)
    },
  ) => this.req<Project>('PATCH', `/api/projects/${enc(id)}`, body)
  // ---- account-projects: encrypted accounts only (installed by installPrivateWork) ----
  createAccountProject = async (_body: {
    name: string
    goal?: string
    remote: string | null
  }): Promise<Project> => {
    throw new Error('Account projects need an encrypted account.')
  }
  recordCheckouts = async (
    _id: string,
    _body: {
      machineIds: string[]
      statementId?: string
      via: 'checkout' | 'adopt'
      remote?: string
    },
  ): Promise<Project> => {
    throw new Error('Account projects need an encrypted account.')
  }
  // ---- end account-projects ----

  ideas = (q: { status?: string; projectId?: string } = {}) =>
    this.req<Idea[]>('GET', `/api/ideas${qs(q)}`)
  createIdea = (idea: {
    text: string
    projectId?: string
    workstreamId?: string
    mode?: 'idea' | 'direct'
    attachments?: string[]
  }) => this.req<Idea>('POST', '/api/ideas', idea)
  updateIdea = (id: string, patch: { text?: string; status?: 'archived' | 'done' | 'new' }) =>
    this.req<Idea>('PATCH', `/api/ideas/${enc(id)}`, patch)
  triageIdea = (id: string) => this.req<BrainCommand>('POST', `/api/ideas/${enc(id)}/triage`, {})
  /**
   * Capture: JSON, or multipart when files are attached (bytes go to R2 before the idea is
   * recorded; 503 `storage_not_configured` without R2). Idea mode returns a `brain.triage`
   * command the caller signs with the device key and submits.
   */
  capture = async (input: {
    text: string
    mode?: 'idea' | 'direct'
    target?: 'auto' | { projectId?: string; workstreamId?: string }
    files?: File[]
    origin?: { buildId?: string }
  }): Promise<CaptureResult> => {
    if (!input.files?.length) {
      return this.req<CaptureResult>('POST', '/api/capture', {
        text: input.text,
        mode: input.mode ?? 'idea',
        target: input.target ?? 'auto',
        ...(input.origin ? { origin: input.origin } : {}),
      })
    }
    const form = new FormData()
    form.set('text', input.text)
    form.set('mode', input.mode ?? 'idea')
    const t = input.target && input.target !== 'auto' ? input.target : undefined
    if (t?.projectId) form.set('projectId', t.projectId)
    if (t?.workstreamId) form.set('workstreamId', t.workstreamId)
    if (input.origin?.buildId) form.set('buildId', input.origin.buildId)
    for (const f of input.files) form.append('file', f, f.name || 'file')
    return this.raw<CaptureResult>('POST', '/api/capture', form).catch(storageGuard)
  }
  brainJobs = () => this.req<BrainCommand[]>('GET', '/api/brain/jobs')

  workstreams = (q: { projectId?: string; status?: string } = {}) =>
    this.req<WorkstreamDetail[]>('GET', `/api/workstreams${qs(q)}`)
  workstream = (id: string) => this.req<WorkstreamDetail>('GET', `/api/workstreams/${enc(id)}`)
  createWorkstream = (body: {
    projectId: string
    name: string
    brief?: string
    branch?: string
    machineId?: string
  }) => this.req<WorkstreamDetail>('POST', '/api/workstreams', body)
  updateWorkstream = (
    id: string,
    patch: { name?: string; brief?: string; status?: string; paused?: boolean },
  ) => this.req<WorkstreamDetail>('PATCH', `/api/workstreams/${enc(id)}`, patch)
  approveProposed = (id: string, body: { standing?: boolean; taskIds?: string[] } = {}) =>
    this.req<{ approved: string[]; standing: boolean }>(
      'POST',
      `/api/workstreams/${enc(id)}/approve-proposed`,
      body,
    )

  /** g18-batch: ready a plan's proposed tasks (all, or `taskIds`) in one request. */
  approvePlan = (planId: string, body: { taskIds?: string[] } = {}) =>
    this.req<{ approved: string[] }>('POST', `/api/plans/${enc(planId)}/approve`, body)

  tasks = (q: { projectId?: string; workstreamId?: string; status?: string } = {}) =>
    this.req<TaskDetail[]>('GET', `/api/tasks${qs(q)}`)
  task = (id: string) => this.req<TaskDetail>('GET', `/api/tasks/${enc(id)}`)
  createTask = (body: {
    projectId?: string
    workstreamId?: string
    title: string
    description?: string
    deps?: string[]
    acceptance?: string[]
    labels?: string[]
    priority?: number
  }) => this.req<TaskDetail>('POST', '/api/tasks', body)
  updateTask = (
    id: string,
    patch: Partial<
      Pick<
        TaskDetail,
        | 'title'
        | 'description'
        | 'status'
        | 'priority'
        | 'labels'
        | 'deps'
        | 'acceptance'
        | 'routeHint'
      >
    >,
  ) => this.req<TaskDetail>('PATCH', `/api/tasks/${enc(id)}`, patch)
  approveTask = (id: string) => this.req<TaskDetail>('POST', `/api/tasks/${enc(id)}/approve`, {})

  rules = (q: { projectId?: string; workstreamId?: string } = {}) =>
    this.req<Rule[]>('GET', `/api/rules${qs(q)}`)
  createRule = (body: {
    text: string
    scope: 'global' | 'project' | 'workstream'
    projectId?: string
    workstreamId?: string
  }) => this.req<Rule>('POST', '/api/rules', body)
  updateRule = (id: string, patch: { text?: string; status?: 'active' | 'retired' | 'proposed' }) =>
    this.req<Rule>('PATCH', `/api/rules/${enc(id)}`, patch)

  landings = (q: { projectId?: string; workstreamId?: string; state?: string } = {}) =>
    this.req<Landing[]>('GET', `/api/landings${qs(q)}`)
  builds = (q: { projectId?: string; laneId?: string } = {}) =>
    this.req<BuildCard[]>('GET', `/api/builds${qs(q)}`)
  build = (id: string) => this.req<BuildCard>('GET', `/api/builds/${enc(id)}`)
  buildFeedback = (id: string, body: { text: string; taskId?: string; attachmentIds?: string[] }) =>
    this.req<{ feedbackId: string; status: string; taskId: string | null; ideaId: string | null }>(
      'POST',
      `/api/builds/${enc(id)}/feedback`,
      body,
    )

  prepareUsageCard = (id: string) =>
    this.req<{ runId: string; taskId: string; machineId: string; agent: 'claude' | 'codex' }[]>(
      'POST',
      `/api/cards/${enc(id)}/usage-prepare`,
      {},
    )
  usage = () => this.req<UsageWindow[]>('GET', '/api/usage')
  hygiene = () => this.req<HygieneReport>('GET', '/api/hygiene')
  /** g2g3-alerts: active device/usage alerts; only encrypted accounts have them (see encrypted-reports). */
  deviceAlerts: () => Promise<import('@viko/protocol').DeviceAlertReport[]> = async () => []
  // ---- g13-digests: per-project changelogs + digests (encrypted accounts only; see encrypted-reports) ----
  changelogs: () => Promise<import('@viko/protocol').ChangelogReport[]> = async () => []
  digests: () => Promise<import('@viko/protocol').DigestReport[]> = async () => []
  // ---- end g13-digests ----
  schedMachines = () => this.req<SchedMachine[]>('GET', '/api/scheduler/machines')
  metrics = (days = 7) => this.req<Metrics>('GET', `/api/metrics?days=${days}`)
  /** burst-guard: today's account request budget; `null` where the hub has no budget route. */
  budget = async (): Promise<HubBudget | null> => {
    try {
      return await this.req<HubBudget>('GET', '/api/budget')
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }
  askStatus = (q: string) => this.req<StatusAnswer>('POST', '/api/ask-status', { q })

  inboxRanked = () => this.req<RankedInbox>('GET', '/api/inbox/ranked')
  /** Accepts a bare question/card id, or a hub item id (`q:…` / `c:…`). */
  snooze = (id: string, untilMs: number) =>
    this.req<{ ok: boolean; until: number }>('POST', `/api/inbox/${enc(id)}/snooze`, {
      until: untilMs,
    })
  // ---- g-inbox: private asks and browser-derived card snoozes ----
  privateNotification = async (id: string) =>
    PrivateNotification.parse(await this.req('GET', `/api/notifications/${enc(id)}`))
  answerClaim = async (id: string, commandId: string) =>
    AnswerClaimResult.parse(
      await this.req('POST', `/api/inbox/${enc(id)}/answer-claim`, { commandId }),
    )
  cardSnoozes = () => this.req<Record<string, number>>('GET', '/api/inbox-snoozes')
  // ---- end g-inbox ----
  card = (id: string) => this.req<Card>('GET', `/api/cards/${enc(id)}`)
  cardOp = (id: string, op: CardOp) =>
    this.req<unknown>('POST', `/api/cards/${enc(id)}/ops`, { op })
  cardAct = (
    id: string,
    action: CardAction,
    extra: { until?: number; standing?: boolean; proposalIds?: string[] } = {},
  ) => this.req<unknown>('POST', `/api/cards/${enc(id)}/act`, { action, ...extra })

  automation = () => this.req<{ enabled: boolean }>('GET', '/api/automation')
  setAutomation = (enabled: boolean) =>
    this.req<{ enabled: boolean }>('PUT', '/api/automation', { enabled })
  grants = () => this.req<GrantRow[]>('GET', '/api/grants')
  addGrant = (grant: DispatchGrant) => this.req<GrantRow>('POST', '/api/grants', { grant })
  revokeGrants = (revocation: GrantRevocation) =>
    this.req<{ revoked: string[] }>('POST', '/api/grants/revoke', { revocation })
  // ---- land-grant: standing landing grants (docs/e2e.md "Standing landing grants") ----
  landGrants = (projectId?: string) =>
    this.req<import('@viko/protocol').LandGrantRow[]>(
      'GET',
      `/api/land-grants${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`,
    )
  addLandGrant = (grant: import('@viko/protocol').LandGrantProof) =>
    this.req<import('@viko/protocol').LandGrantRow>('POST', '/api/land-grants', { grant })
  revokeLandGrants = (grantIds: string[]) =>
    this.req<{ revoked: string[] }>('POST', '/api/land-grants/revoke', { grantIds })
  // ---- end land-grant ----

  settings = () => this.req<Settings>('GET', '/api/settings')
  saveSettings = (s: Settings) => this.req<Settings>('PUT', '/api/settings', s)
  /** Push payloads carry only ids; the service worker and app fetch details here. */
  notification = (id: string) =>
    this.req<{
      id: string
      title: string
      body: string
      url: string
      kind: string
      hidePreviews?: boolean
    }>('GET', `/api/notifications/${enc(id)}`)

  /** Multipart / non-JSON body variant of `req` (no Content-Type: the browser sets the boundary). */
  private raw<T>(method: string, path: string, body: BodyInit): Promise<T> {
    return this.req<T>(method, path, undefined, {}, body)
  }
}

/** Artifact storage (R2) is not enabled on this deployment. */
export class StorageNotConfigured extends Error {
  readonly code = 'storage_not_configured'
  constructor() {
    super('File storage is not enabled on this Viko server yet. Send text only for now.')
  }
}

function storageGuard(e: unknown): never {
  if (e instanceof ApiError && (e.code === 'storage_not_configured' || e.status === 503))
    throw new StorageNotConfigured()
  throw e
}

const enc = encodeURIComponent

function qs(q: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { error: 'bad_json', message: text.slice(0, 200) }
  }
}

/** Upload a file to a presigned artifact URL (separate cookieless origin). */
export async function putArtifact(fetcher: Fetcher, url: string, file: Blob): Promise<void> {
  const res = await fetcher(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    credentials: 'omit',
  })
  if (!res.ok) throw new ApiError(res.status, 'upload_failed', `Upload failed (${res.status})`)
}

/**
 * `GET /api/runs` rows are snake_case SQLite rows (`id, machine_id, head_seq, updated_at`);
 * the WS `run` frame and the mock are camelCase. Accept both.
 */
export function normalizeRun(r: Record<string, unknown>): RunSummary {
  const pick = <T>(...keys: string[]) => {
    for (const k of keys) if (r[k] !== undefined && r[k] !== null) return r[k] as T
    return undefined
  }
  const out = { ...r } as Record<string, unknown>
  for (const k of ['id', 'machine_id', 'head_seq', 'updated_at']) delete out[k]
  return {
    ...(out as Partial<RunSummary>),
    runId: pick<string>('runId', 'id') ?? '',
    machineId: pick<string>('machineId', 'machine_id') ?? '',
    status: pick<string>('status') ?? 'idle',
    headSeq: pick<number>('headSeq', 'head_seq') ?? 0,
    ...(pick<number>('updatedAt', 'updated_at') !== undefined
      ? { updatedAt: pick<number>('updatedAt', 'updated_at') }
      : {}),
  } as RunSummary
}

/**
 * `GET /api/machines` doctor rows: the hub maps the daemon's FixCards (`checkId, severity,
 * title, problem, fix{text, command, autoFix}`) loosely; accept both that and the flat shape.
 */
export function normalizeMachine(m: Record<string, unknown>): Machine {
  const accounts = ProviderAccountsMetadata.safeParse(m.providerAccounts)
  const { providerAccounts: _accounts, ...rest } = m
  const doctor = Array.isArray(m.doctor) ? (m.doctor as Record<string, unknown>[]) : undefined
  return {
    ...(rest as unknown as Machine),
    ...(accounts.success ? { providerAccounts: accounts.data } : {}),
    ...(doctor ? { doctor: doctor.map(normalizeDoctor) } : {}),
  }
}

export function normalizeDoctor(d: Record<string, unknown>, i = 0): DoctorCheck {
  const fixObj =
    d.fix && typeof d.fix === 'object'
      ? (d.fix as { text?: string; command?: string; autoFix?: boolean })
      : undefined
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)
  const checkId =
    str(d.checkId) ?? (str(d.id)?.startsWith('fix-') ? undefined : str(d.id)) ?? str(d.check)
  const sev = str(d.status) ?? str(d.severity)
  const status: DoctorCheck['status'] =
    sev === 'ok' || sev === 'pass' ? 'ok' : sev === 'warn' || sev === 'warning' ? 'warn' : 'fail'
  const fix = str(d.fix) ?? fixObj?.text
  const command = str(d.command) ?? fixObj?.command
  const detail = str(d.detail) ?? str(d.problem)
  return {
    id: str(d.id) ?? checkId ?? `check-${i}`,
    ...(checkId ? { checkId } : {}),
    title: str(d.title) ?? checkId ?? 'Check',
    status,
    ...(detail ? { detail } : {}),
    ...(fix ? { fix } : {}),
    ...(command ? { command } : {}),
    ...(fixObj?.autoFix || d.autoFix === true ? { autoFix: true } : {}),
  }
}
